/**
 * Source of truth for backend load statuses (the AI dispatch pipeline:
 * recommended → emailed → negotiating → booked, plus failure / processing
 * variants). Two concerns, intentionally split:
 *
 *   1. `LOAD_STATUS_LABELS` — raw-status → display label. Consumed by both the
 *      per-status colored chip (shared LoadsTable LoadRow) and the
 *      simplified-bucket UI (agentic-hub LoadRowSummary tooltip), since both
 *      need to surface the underlying status name even when the chip color
 *      collapses 19 statuses into 5 buckets.
 *
 *   2. `LOAD_STATUS_PALETTE` — raw-status → Tailwind bg/text tokens. Only
 *      consumed by features that render one chip per raw status (LoadRow);
 *      agentic-hub deliberately ignores this because its chip color comes
 *      from the simplified 5-bucket map (`getSimplifiedStatusStyle`).
 *
 * Lives outside `components/ds/` because the data is load-domain knowledge,
 * not a generic DS token. <StatusChip> is the dumb presentational component;
 * these maps own the load-status semantics.
 */

const LOAD_STATUS_LABELS: Record<string, string> = {
    recommended: 'Recommended',
    emailed: 'Emailed',
    negotiating: 'Negotiating',
    ready_to_negotiate: 'Ready to Negotiate',
    ready_to_book: 'Ready to Book',
    booked: 'Booked',
    called: 'Called',
    voicemail: 'Voicemail',
    rejected: 'Rejected',
    load_cancelled: 'Load Cancelled',
    attention_needed: 'Attention',
    // Backend reports the pre-ranking step as `processing`; main labelled it
    // "Filtering" in the UI because that reads better to dispatchers.
    processing: 'Filtering',
    ranking: 'Ranking',
    potential: 'Potential',
    not_matched: 'Not Matched',
    matched: 'Matched',
    processing_failed: 'Processing Failed',
    duplicate: 'Duplicate',
    filtering: 'Filtering',
    attention: 'Attention',
    // `search-flow-experiments-v2` skips scoring; matched survivors land here.
    // Surfaced as "Open" so dispatchers see them as actionable, not pre-judged.
    unscored: 'Open',
}

interface LoadStatusPaletteEntry {
    bg: string
    text: string
}

const LOAD_STATUS_PALETTE: Record<string, LoadStatusPaletteEntry> = {
    recommended: { bg: 'bg-blue-50 dark:bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400' },
    emailed: { bg: 'bg-sky-50 dark:bg-sky-500/10', text: 'text-sky-600 dark:text-sky-400' },
    negotiating: { bg: 'bg-violet-50 dark:bg-violet-500/10', text: 'text-violet-600 dark:text-violet-400' },
    ready_to_negotiate: { bg: 'bg-orange-50 dark:bg-orange-500/10', text: 'text-orange-600 dark:text-orange-400' },
    ready_to_book: { bg: 'bg-emerald-50 dark:bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400' },
    booked: { bg: 'bg-green-100 dark:bg-green-500/20', text: 'text-green-700 dark:text-green-300' },
    called: { bg: 'bg-teal-50 dark:bg-teal-500/10', text: 'text-teal-600 dark:text-teal-400' },
    voicemail: { bg: 'bg-slate-50 dark:bg-slate-500/10', text: 'text-slate-600 dark:text-slate-400' },
    rejected: { bg: 'bg-red-50 dark:bg-red-500/10', text: 'text-red-600 dark:text-red-400' },
    load_cancelled: { bg: 'bg-rose-50 dark:bg-rose-500/10', text: 'text-rose-600 dark:text-rose-400' },
    attention_needed: { bg: 'bg-amber-50 dark:bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400' },
    processing: { bg: 'bg-indigo-50 dark:bg-indigo-500/10', text: 'text-indigo-600 dark:text-indigo-400' },
    ranking: { bg: 'bg-emerald-50 dark:bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400' },
    potential: { bg: 'bg-amber-50 dark:bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400' },
    not_matched: { bg: 'bg-slate-100 dark:bg-slate-800', text: 'text-slate-500 dark:text-slate-400' },
    matched: { bg: 'bg-green-50 dark:bg-green-500/10', text: 'text-green-600 dark:text-green-400' },
    processing_failed: { bg: 'bg-red-50 dark:bg-red-500/10', text: 'text-red-600 dark:text-red-400' },
    duplicate: { bg: 'bg-yellow-50 dark:bg-yellow-500/10', text: 'text-yellow-600 dark:text-yellow-400' },
    filtering: { bg: 'bg-indigo-50 dark:bg-indigo-500/10', text: 'text-indigo-600 dark:text-indigo-400' },
    attention: { bg: 'bg-amber-50 dark:bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400' },
    // Neutral slate for "Open" — visually says "no AI verdict; user decides".
    unscored: { bg: 'bg-slate-50 dark:bg-slate-500/10', text: 'text-slate-600 dark:text-slate-400' },
}

/** Slate palette fallback for unknown status keys. */
const DEFAULT_LOAD_STATUS_PALETTE: LoadStatusPaletteEntry = {
    bg: 'bg-slate-100 dark:bg-slate-800',
    text: 'text-slate-600 dark:text-slate-400',
}

const titleCaseStatus = (status: string): string => status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

/** Resolve label only. Returns `''` for null/undefined, title-cased status key
 *  for unknown keys. */
export const resolveLoadStatusLabel = (status: string | null | undefined): string => {
    if (!status) return ''
    return LOAD_STATUS_LABELS[status] ?? titleCaseStatus(status)
}

/** Resolve palette only. Returns the slate fallback for null/undefined/unknown
 *  keys. */
const resolveLoadStatusPalette = (status: string | null | undefined): LoadStatusPaletteEntry => {
    if (!status) return DEFAULT_LOAD_STATUS_PALETTE
    return LOAD_STATUS_PALETTE[status] ?? DEFAULT_LOAD_STATUS_PALETTE
}

/** Combined label + palette, for consumers (shared LoadRow) that need all
 *  three to feed `<StatusChip>` in one shot. */
export const resolveLoadStatusStyle = (status: string | null | undefined): LoadStatusPaletteEntry & { label: string } => ({
    ...resolveLoadStatusPalette(status),
    label: resolveLoadStatusLabel(status),
})

// ─── AI fit-classification ──────────────────────────────────────────────────
// Orthogonal to the WORKFLOW status. The workflow status tracks where a load is in the dispatch
// pipeline (open → emailed → booked …); the classification is the AI's verdict on how well the load
// fits THIS SEARCH (recommended / matched / potential), plus the two terminal "not a fit" reasons
// (not_matched / duplicate). `base_tier` is the only source of truth — set once by scoring, never
// mutated, so a load that's since been emailed still reports the tier it was ranked into.
